import { BarChart3, Database, FileSpreadsheet, ShieldCheck, Store, Zap } from 'lucide-react';
import Container from '../../common/Container';
import ScrollReveal from '../common/ScrollReveal';

const pharmacyFeatures = [
  {
    icon: Store,
    title: '100% Standalone & Independent',
    description:
      'Operate completely autonomously with zero external software or hospital dependencies. Ideal for standalone chemist shops, retail medical stores, and multi-branch pharmacy chains.',
  },
  {
    icon: Zap,
    title: 'Sub-Minute Counter Dispensing',
    description:
      'Lightning-fast POS interface with instant barcode scanning, generic formulation lookup, batch selection, and automatic strip-to-loose tablet conversion.',
  },
  {
    icon: ShieldCheck,
    title: 'Zero-Expiry Waste Protection',
    description:
      'Proactive expiry radar highlights medicines reaching expiration in 30, 60, or 90 days. Practice strict FEFO (First-Expiry-First-Out) fulfillment to eliminate stock losses.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Automated GST & Statutory Ledgers',
    description:
      'Fully compliant with Drug Authority mandates (Form 20B/21B) and multi-slab GST (0%, 5%, 12%, 18%). Generates instant GSTR-1 and GSTR-3B audit summaries.',
  },
  {
    icon: Database,
    title: '3D Shelf & Rack Coordinates',
    description:
      'Physical storage locator routing displays exact Block, Column, and Shelf numbers on billing screens for instantaneous medication retrieval by counter staff.',
  },
  {
    icon: BarChart3,
    title: 'Procurement Intelligence & Reorders',
    description:
      'Dynamic reorder formulas calculate consumption velocity and auto-generate Purchase Orders before fast-moving medicines run out of stock.',
  },
];

export const PharmacyFeatures = () => {
  return (
    <section id="features" className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#0D7893]/20 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none" />

      <Container>
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <ScrollReveal direction="down">
            <span className="inline-flex items-center gap-1 px-3.5 py-1 rounded-full bg-[#0D7893]/20 text-[#38bcd8] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/40">
              Why Choose Divine Pharmacy ERP
            </span>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              Engineered for Complete Pharmacy & Inventory Control
            </h2>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={200}>
            <p className="text-slate-400 text-base md:text-lg">
              Designed specifically for standalone retail pharmacies, chemist shops, and wholesale dispensaries to maximize counter speed, eliminate expired inventory loss, and maintain flawless regulatory compliance.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pharmacyFeatures.map((item, idx) => {
            const Icon = item.icon;
            return (
              <ScrollReveal
                key={idx}
                direction="up"
                delay={idx * 100}
                className="group relative p-8 rounded-3xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-[#0D7893]/50 transition-all duration-300 shadow-xl hover:shadow-[#0D7893]/10"
              >
                <div className="p-3.5 rounded-2xl bg-[#0D7893]/20 text-[#38bcd8] w-fit mb-6 group-hover:bg-[#0D7893] group-hover:text-white transition-all duration-300">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </ScrollReveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default PharmacyFeatures;
